import { endOfDay } from 'date-fns';

function parseAnalysedDataEndDate(value: string): Date | null {
  const dateOnly = value.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * When a project pins analysed_data_end_date, relative custom time ranges resolve against
 * end of that calendar day (request timezone) as effective "today".
 */
export function getEffectiveNow(
  analysedDataEndDate: string | null | undefined,
  fallbackNow: Date = new Date(),
): Date {
  if (!analysedDataEndDate) {
    return fallbackNow;
  }

  const parsed = parseAnalysedDataEndDate(analysedDataEndDate);
  if (!parsed) {
    return fallbackNow;
  }

  return endOfDay(parsed);
}

/** Format analysed_data_end_date for `<input type="date">` without UTC shift. */
export function formatAnalysedDataEndDateForInput(value: string | null | undefined): string {
  if (!value) return '';
  const dateOnly = value.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return dateOnly;
  }

  const parsed = parseAnalysedDataEndDate(value);
  if (!parsed) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Display analysed_data_end_date as DD/MM/YY in the app shell indicator. */
export function formatAnalysedDataEndDateForDisplay(value: string | null | undefined): string {
  const parsed = value ? parseAnalysedDataEndDate(value) : null;
  if (!parsed) return '';

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = String(parsed.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

export function isFutureAnalysedDataEndDate(
  dateInput: string,
  referenceNow: Date = new Date(),
): boolean {
  if (!dateInput.trim()) return false;

  const parsed = parseAnalysedDataEndDate(dateInput);
  if (!parsed) return false;

  const referenceDay = new Date(referenceNow.getFullYear(), referenceNow.getMonth(), referenceNow.getDate());
  return parsed.getTime() > referenceDay.getTime();
}
